import {
  Checkbox,
  FormLayout,
  Icon,
  InlineStack,
  Link,
  Popover,
  PopoverContent,
  PopoverContentDescription,
  PopoverContentHeader,
  PopoverTrigger,
  Stack,
  TextField,
  type StackProps,
} from '@customerio/pluma-components/react';
import type { Ref } from 'react';
import { CredentialFileField } from './credential-file-field';
import * as styles from '../styles.css';

/**
 * Where the fields are being shown. The push settings page configures the
 * workspace's single APNS credential; the mobile app editor configures one app's
 * own credential, which identifies itself by its own app identifier.
 */
export type CredentialFieldsVariant = 'settings' | 'app';

/** A message for each value a save found missing; a field without one is fine. */
export type ApnsCredentialFieldErrors = Partial<Record<'file' | 'keyId' | 'teamId' | 'bundleId', string>>;

export type ApnsCredentialFieldsProps = {
  variant: CredentialFieldsVariant;
  bundleId: string;
  keyId: string;
  teamId: string;
  sandbox: boolean;
  /** The file the user has picked but not yet saved. */
  selectedFileName: string | null;
  /** The file name stored alongside the saved credential. */
  savedFileName: string | null;
  /** Whether a new credential file may be chosen. */
  isEditable: boolean;
  /** Whether the text fields and the file picker are locked. */
  isDisabled: boolean;
  /** Whether the sandbox checkbox is locked. */
  isSandboxDisabled: boolean;
  /** Marks the credential file as what a failed save is complaining about. */
  isInvalid?: boolean;
  errors?: ApnsCredentialFieldErrors;
  ref?: Ref<HTMLDivElement>;
  onBundleIdChange: (value: string) => void;
  onKeyIdChange: (value: string) => void;
  onTeamIdChange: (value: string) => void;
  onSandboxChange: (checked: boolean) => void;
  onFileSelected: (file: File) => void;
} & StackProps;

const SANDBOX_LABEL = 'Send all push notifications to sandbox';

/** An app's fields share a page with its Firebase ones, so they carry a prefix. */
const VARIANT_FIELDS = {
  settings: { idPrefix: '', bundleIdLabel: 'Bundle ID' },
  app: { idPrefix: 'apns_', bundleIdLabel: 'Bundle ID / App identifier' },
} satisfies Record<CredentialFieldsVariant, { idPrefix: string; bundleIdLabel: string }>;

/**
 * The four values an APNS authorization is made of — the signing key file and
 * the three identifiers that go with it — plus the sandbox switch.
 *
 * Fully controlled: the page that owns the values decides what a change means,
 * including whether switching the sandbox needs confirming first.
 */
export function ApnsCredentialFields({
  variant,
  bundleId,
  keyId,
  teamId,
  sandbox,
  selectedFileName,
  savedFileName,
  isEditable,
  isDisabled,
  isSandboxDisabled,
  isInvalid = false,
  errors = {},
  onBundleIdChange,
  onKeyIdChange,
  onTeamIdChange,
  onSandboxChange,
  onFileSelected,
  ...boxProps
}: ApnsCredentialFieldsProps) {
  const { idPrefix: prefix, bundleIdLabel } = VARIANT_FIELDS[variant];

  const fileField = (
    <CredentialFileField
      // The card is taller than a text field, and the identifiers that follow it
      // read as belonging to it without this much air between the two.
      mb="200"
      provider="apns"
      selectedFileName={selectedFileName}
      savedFileName={savedFileName}
      isEditable={isEditable}
      isDisabled={isDisabled}
      isInvalid={isInvalid}
      error={errors.file}
      onFileSelected={onFileSelected}
    />
  );

  const bundleIdField = (
    <TextField
      label={bundleIdLabel}
      id={`${prefix}bundle_id`}
      value={bundleId}
      error={errors.bundleId}
      isDisabled={isDisabled}
      placeholder="com.sample.app"
      onInput={(event) => onBundleIdChange(event.target.value)}
    />
  );

  const keyIdField = (
    <TextField
      label="Key ID"
      id={`${prefix}key_id`}
      value={keyId}
      error={errors.keyId}
      isDisabled={isDisabled}
      placeholder="abcde12345"
      onInput={(event) => onKeyIdChange(event.target.value)}
    />
  );

  const teamIdField = (
    <TextField
      label="Team ID"
      id={`${prefix}team_id`}
      value={teamId}
      error={errors.teamId}
      isDisabled={isDisabled}
      placeholder="abcde12345"
      onInput={(event) => onTeamIdChange(event.target.value)}
    />
  );

  if (variant === 'app') {
    return (
      <Stack gap="200" {...boxProps}>
        {bundleIdField}
        {fileField}
        {keyIdField}
        {teamIdField}
        <Checkbox
          label={SANDBOX_LABEL}
          name="apns_sandbox"
          isChecked={sandbox}
          isDisabled={isSandboxDisabled}
          onCheckedChange={onSandboxChange}
        />
      </Stack>
    );
  }

  return (
    <FormLayout {...boxProps}>
      {fileField}
      {keyIdField}
      {teamIdField}
      {bundleIdField}

      <InlineStack gap="100">
        <Checkbox
          label={SANDBOX_LABEL}
          name="send_all_to_sandbox"
          isChecked={sandbox}
          isDisabled={isSandboxDisabled}
          onCheckedChange={onSandboxChange}
        />

        <Popover shouldTriggerOnClick={false} shouldTriggerOnHover={true} isFocusManagerDisabled={true}>
          <PopoverTrigger type="button" cursor="help" aria-label="Show sandbox description">
            <Icon name="help" color="accent" />
          </PopoverTrigger>

          <PopoverContent className={styles.sandboxPopover}>
            <PopoverContentHeader mb="100">Sandbox Environment</PopoverContentHeader>

            <PopoverContentDescription>
              Checking this option means we'll send your push notifications using the APNs sandbox endpoint.{' '}
              <Link href="https://docs.customer.io/push-qa-testing/" variant="secondary" isExternal={true}>
                Learn more…
              </Link>
            </PopoverContentDescription>
          </PopoverContent>
        </Popover>
      </InlineStack>
    </FormLayout>
  );
}
